import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ErrorMessage from '../components/ui/ErrorMessage';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import mcqService from '../services/mcqService';

/**
 * MCQTest - Full-screen MCQ taking interface
 * Shows questions with up to 5 options (A-E), navigation, and submit functionality
 */
const MCQTest = () => {
  const { mcqId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [mcq, setMcq] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [attemptId, setAttemptId] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Fetch MCQ and start attempt on mount
  useEffect(() => {
    if (isAuthenticated && mcqId) {
      startMCQ();
    }
  }, [isAuthenticated, mcqId]);

  /**
   * Start MCQ: fetch questions and create attempt
   */
  const startMCQ = async () => {
    try {
      setLoading(true);
      setError(null);

      // Start MCQ (get randomized questions)
      const mcqData = await mcqService.startMCQ(mcqId);
      setMcq(mcqData);
      setQuestions(mcqData.questions);

      // Create attempt
      const attemptData = await mcqService.startMCQAttempt(mcqId);
      setAttemptId(attemptData.id);

      // Initialize answers object
      const initialAnswers = {};
      mcqData.questions.forEach(q => {
        initialAnswers[q.id] = null;
      });
      setAnswers(initialAnswers);
    } catch (err) {
      console.error('Failed to start MCQ:', err);
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to load MCQ. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle answer selection
   */
  const handleAnswerSelect = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  /**
   * Navigate to next question
   */
  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  /**
   * Navigate to previous question
   */
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  /**
   * Handle submit button click
   */
  const handleSubmitClick = () => {
    // Check if all questions are answered
    const unansweredCount = Object.values(answers).filter(a => a === null).length;
    if (unansweredCount > 0) {
      setError(`You have ${unansweredCount} unanswered question(s). Please answer all questions before submitting.`);
      return;
    }
    setShowSubmitDialog(true);
  };

  /**
   * Submit MCQ answers
   */
  const handleSubmitConfirm = async () => {
    setShowSubmitDialog(false);

    try {
      setSubmitting(true);
      setError(null);

      // Convert answers object to array format required by API
      const answersArray = Object.entries(answers).map(([questionId, selectedAnswer]) => ({
        question_id: parseInt(questionId),
        selected_answer: selectedAnswer
      }));

      // Submit answers
      const result = await mcqService.submitMCQAttempt(attemptId, answersArray);

      // Navigate to results page
      navigate(`/mcq-results/${result.id}`);
    } catch (err) {
      console.error('Failed to submit MCQ:', err);
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to submit MCQ. Please try again.');
      setSubmitting(false);
    }
  };

  // Get current question
  const currentQuestion = questions[currentQuestionIndex];

  // Calculate progress
  const answeredCount = Object.values(answers).filter(a => a !== null).length;
  const progressPercentage = (answeredCount / questions.length) * 100;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <LoadingSpinner message="Loading MCQ..." />
        <Footer />
      </div>
    );
  }

  // Error state
  if (error && !mcq) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="container mx-auto max-w-4xl px-4 py-16">
          <ErrorMessage message={error} onRetry={startMCQ} />
          <div className="text-center mt-6">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-grow max-w-4xl mx-auto px-4 py-8 w-full">
        {/* MCQ Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{mcq?.title}</h1>
              {mcq?.description && (
                <p className="text-gray-600 mt-1">{mcq.description}</p>
              )}
            </div>
            <button
              onClick={() => navigate(-1)}
              className="text-gray-500 hover:text-gray-700"
              title="Exit MCQ"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2 text-sm text-gray-600">
              <span>Progress: {answeredCount} / {questions.length} answered</span>
              <span>{progressPercentage.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-blue-600 to-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          {/* Question Counter */}
          <div className="text-sm text-gray-500">
            Question {currentQuestionIndex + 1} of {questions.length}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <ErrorMessage message={error} onClose={() => setError(null)} />
        )}

        {/* Current Question */}
        {currentQuestion && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {currentQuestion.question_text}
            </h2>

            {/* Options */}
            <div className="space-y-3">
              {['A', 'B', 'C', 'D', 'E'].map(option => {
                const optionValue = currentQuestion[`option_${option.toLowerCase()}`];
                const isSelected = answers[currentQuestion.id] === option;

                // Skip if option E is empty (for backward compatibility)
                if (option === 'E' && (!optionValue || optionValue.trim() === '')) {
                  return null;
                }

                return (
                  <button
                    key={option}
                    onClick={() => handleAnswerSelect(currentQuestion.id, option)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-blue-600 bg-blue-50 shadow-md'
                        : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        isSelected
                          ? 'border-blue-600 bg-blue-600'
                          : 'border-gray-400'
                      }`}>
                        {isSelected && (
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-grow">
                        <span className="font-semibold text-gray-900">{option}.</span>
                        <span className="ml-2 text-gray-700">{optionValue}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between bg-white rounded-lg shadow-md p-6">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </div>
          </button>

          <div className="text-sm text-gray-500">
            {currentQuestionIndex + 1} / {questions.length}
          </div>

          {currentQuestionIndex === questions.length - 1 ? (
            <button
              onClick={handleSubmitClick}
              disabled={submitting}
              className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit MCQ'}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              <div className="flex items-center gap-2">
                Next
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          )}
        </div>
      </main>

      <Footer />

      {/* Submit Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showSubmitDialog}
        title="Submit MCQ"
        message={`Are you sure you want to submit your answers? You have answered ${answeredCount} out of ${questions.length} questions.`}
        confirmText="Submit"
        confirmColor="blue"
        onConfirm={handleSubmitConfirm}
        onClose={() => setShowSubmitDialog(false)}
      />
    </div>
  );
};

export default MCQTest;
